import api, { ApiResponse } from './api';
import type {
  MemberDedupFieldChoices,
  MemberDedupMergeResult,
  MemberDedupPair,
  MemberDedupPairList,
  MyDuplicateCandidate,
} from '@safetyhub/shared';

export type {
  MemberDedupFieldChoices,
  MemberDedupMergeResult,
  MemberDedupPair,
  MemberDedupPairList,
  MyDuplicateCandidate,
};

/**
 * 중복 멤버 판정 (P5) API — 대표 1차 판정(companyId = 클레임한 자기 조직) + 당사자 본인 축.
 * 응답은 신원 필드(이름·전화·이메일)까지만 — 결정 2026-08-14 §4 불변식 3.
 */
export const memberDedupApi = {
  listCandidates: async (companyId: string): Promise<MemberDedupPairList> => {
    const response = await api.get<ApiResponse<MemberDedupPairList>>(
      `/members/duplicates?companyId=${companyId}`,
    );
    return response.data.data ?? { pairs: [], truncated: false };
  },

  merge: async (
    companyId: string,
    input: {
      targetMemberId: string;
      duplicateMemberId: string;
      fieldChoices?: MemberDedupFieldChoices;
    },
  ): Promise<MemberDedupMergeResult> => {
    const response = await api.post<ApiResponse<MemberDedupMergeResult>>(
      `/members/duplicates/merge?companyId=${companyId}`,
      input,
    );
    return response.data.data!;
  },

  dismiss: async (
    companyId: string,
    memberIdA: string,
    memberIdB: string,
  ): Promise<{ pairKey: string }> => {
    const response = await api.post<ApiResponse<{ pairKey: string }>>(
      `/members/duplicates/dismiss?companyId=${companyId}`,
      { memberIdA, memberIdB },
    );
    return response.data.data!;
  },

  unmerge: async (
    companyId: string,
    memberId: string,
  ): Promise<{ memberId: string; reopenedMembership: boolean }> => {
    const response = await api.post<
      ApiResponse<{ memberId: string; reopenedMembership: boolean }>
    >(`/members/duplicates/unmerge?companyId=${companyId}`, { memberId });
    return response.data.data!;
  },

  listMyDuplicates: async (): Promise<MyDuplicateCandidate[]> => {
    const response = await api.get<ApiResponse<MyDuplicateCandidate[]>>('/members/my-duplicates');
    return response.data.data ?? [];
  },

  mergeIntoMe: async (duplicateMemberId: string): Promise<MemberDedupMergeResult> => {
    const response = await api.post<ApiResponse<MemberDedupMergeResult>>(
      '/members/my-duplicates/merge',
      { duplicateMemberId },
    );
    return response.data.data!;
  },

  dismissMyDuplicate: async (duplicateMemberId: string): Promise<{ dismissed: true }> => {
    const response = await api.post<ApiResponse<{ dismissed: true }>>(
      '/members/my-duplicates/dismiss',
      { duplicateMemberId },
    );
    return response.data.data!;
  },
};
